import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

export const exportToExcel = (type: string, data: any) => {
  let filename = `${type}_export.xlsx`;
  const generatedOn = new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(new Date());

  const periodString = data.reportMeta?.period 
    ? data.reportMeta.period 
    : data.reportMeta?.dateRange 
      ? data.reportMeta.dateRange 
      : data.reportMeta?.fromDate && data.reportMeta?.toDate 
        ? `${data.reportMeta.fromDate} to ${data.reportMeta.toDate}` 
        : data.reportMeta?.toDate 
          ? `As of ${data.reportMeta.toDate}` 
          : '';

  const reportTitle = data.reportMeta?.title 
    ? data.reportMeta.title 
    : type === 'daybook' ? 'Day Book'
    : type === 'ledger' ? `Ledger: ${data.party?.name || ''}`
    : type === 'invoice' ? `Invoice: ${data.meta?.invoiceNo || ''}`
    : type === 'trialbalance' ? 'Trial Balance'
    : type === 'balancesheet' ? 'Balance Sheet'
    : type === 'profitloss' ? 'Profit & Loss'
    : type === 'stocksummary' ? 'Stock Summary'
    : type === 'stock' ? `Stock Movement: ${data.item?.name || ''}`
    : type === 'registersummary' ? `${data.reportMeta?.voucherType || ''} Register Summary`
    : type === 'voucherregister' ? `${data.reportMeta?.voucherType || ''} Register`
    : 'Report';

  const companyName = data.company?.name || 'Aarthika Pro';

  const headerRowsRaw = [
    [companyName],
    [reportTitle],
    periodString ? [`Period: ${periodString}`] : null,
    [`Generated On: ${generatedOn}`],
    []
  ];

  const headerRows = headerRowsRaw.filter(row => row !== null) as any[][];

  let aoa: any[][] = [...headerRows];
  let isStandardFormat = ['daybook', 'ledger', 'binbox', 'voucherregister', 'invoice'].includes(type) || (type === 'daybook' && data.reportMeta?.title?.includes('Bin Box'));
  
  const isBinBox = type === 'binbox' || (data.reportMeta?.title && data.reportMeta.title.includes('Bin Box'));
  
  const formatAmount = (val: any) => {
    if (val === null || val === undefined || val === '') return '';
    const num = typeof val === 'string' ? parseFloat(val.replace(/,/g, '')) : Number(val);
    if (isNaN(num)) return val;
    return num.toFixed(2);
  };

  const formatDateStr = (dateString: any) => {
    if (!dateString) return '';
    const str = String(dateString).trim();
    if (/^\d{1,2}[-\/]\d{1,2}[-\/]\d{4}$/.test(str)) {
      const parts = str.split(/[-\/]/);
      return `${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}-${parts[2]}`;
    }
    const d = new Date(str);
    if (!isNaN(d.getTime())) {
      return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
    }
    return str;
  };

  if (isStandardFormat) {
    let cols = ['Date', 'Voucher Type', 'Voucher No', 'Ref No', 'Party / Particulars', 'Narration', 'Debit', 'Credit'];
    if (type === 'ledger') cols.push('Balance');
    if (isBinBox) cols.splice(4, 0, 'Deleted At');
    
    aoa.push(cols);

    let dataSource = [];
    if (type === 'ledger') {
      dataSource = data.transactions || [];
      filename = `Ledger_${data.party?.name}.xlsx`;
    } else if (type === 'invoice') {
      // Force invoice to match structure using its items if we must, or just output one row for the invoice
      dataSource = data.items ? data.items.map((i: any) => ({
        date: data.meta?.date,
        vchType: data.meta?.voucherType || 'Invoice',
        vchNo: data.meta?.invoiceNo,
        refNo: '',
        particulars: i.description,
        narration: `Qty: ${i.quantity} @ ${i.rate} ${i.per}`,
        debit: i.amount,
        credit: ''
      })) : [];
      filename = `Invoice_${data.meta?.invoiceNo}.xlsx`;
    } else {
      dataSource = data.vouchers || [];
      if (isBinBox) filename = `BinBox_${data.reportMeta?.fromDate}_to_${data.reportMeta?.toDate}.xlsx`;
      else if (type === 'voucherregister') filename = `${data.reportMeta?.voucherType}_Register_${data.reportMeta?.period}.xlsx`;
      else filename = `DayBook_${data.reportMeta?.fromDate}_to_${data.reportMeta?.toDate}.xlsx`;
    }

    // Group by Date for standard voucher listings
    let currentGroupDate: string | null = null;
    let hasGrouped = false;

    // Do not group Ledger by date typically since it has continuous balance, but we can spacing. 
    // The prompt says "Group data by date... Add spacing between groups"
    dataSource.forEach((row: any) => {
      const rowDate = formatDateStr(row.date);
      if (rowDate && rowDate !== currentGroupDate && type !== 'invoice') {
        if (hasGrouped) aoa.push([]); // Spacing between groups
        currentGroupDate = rowDate;
        aoa.push([rowDate, '', '', '', '', '', '', '', '']);
        hasGrouped = true;
      }
      
      let mainParticulars = row.particulars;
      if (typeof row.particulars === 'object') {
         mainParticulars = row.particulars.main || '';
      }

      const rowData = [
        rowDate,
        row.vchType || '',
        row.vchNo || '',
        row.refNo || '',
        mainParticulars || '',
        row.narration || '',
        formatAmount(row.debit),
        formatAmount(row.credit)
      ];

      if (type === 'ledger') rowData.push(row.balance || '');
      if (isBinBox) {
        rowData.splice(4, 0, row.deletedAt ? formatDateStr(row.deletedAt) : '');
      }

      aoa.push(rowData);
    });

    aoa.push([]); // Space before totals
    const totalsRow = [
      '', '', '', '', 'Total', '', 
      formatAmount(data.totals?.debit || data.totals?.totalDebit || data.totals?.amount || 0),
      formatAmount(data.totals?.credit || data.totals?.totalCredit || 0)
    ];
    if (type === 'ledger') totalsRow.push(data.totals?.balance || '');
    if (isBinBox) totalsRow.splice(4, 0, '');

    aoa.push(totalsRow);

  } else if (type === 'trialbalance') {
    filename = `TrialBalance_${data.reportMeta?.fromDate}_to_${data.reportMeta?.toDate}.xlsx`;
    aoa.push(['Particulars', 'Debit', 'Credit']);
    (data.items || []).forEach((i: any) => {
      aoa.push(['  '.repeat(i.depth || 0) + i.name, formatAmount(i.debit), formatAmount(i.credit)]);
    });
    aoa.push([]);
    aoa.push(['Total', formatAmount(data.totalDebit), formatAmount(data.totalCredit)]);
  } else if (type === 'balancesheet') {
    filename = `BalanceSheet_AsOf_${data.reportMeta?.toDate}.xlsx`;
    aoa.push(['Liabilities', 'Amount', 'Assets', 'Amount']);
    const maxLen = Math.max((data.liabilities || []).length, (data.assets || []).length);
    for (let i = 0; i < maxLen; i++) {
      const l = data.liabilities[i];
      const a = data.assets[i];
      aoa.push([
        l ? ('  '.repeat(l.depth || 0) + l.name) : '',
        l && l.amount !== undefined ? formatAmount(l.amount) : '',
        a ? ('  '.repeat(a.depth || 0) + a.name) : '',
        a && a.amount !== undefined ? formatAmount(a.amount) : ''
      ]);
    }
    aoa.push([]);
    aoa.push(['Total', formatAmount(data.totalLiabilities), 'Total', formatAmount(data.totalAssets)]);
  } else if (type === 'profitloss') {
    filename = `ProfitLoss_${data.reportMeta?.fromDate}_to_${data.reportMeta?.toDate}.xlsx`;
    aoa.push(['Particulars', 'Expenses', 'Particulars ', 'Incomes']);
    const maxLen = Math.max((data.expenses || []).length, (data.incomes || []).length);
    for (let i = 0; i < maxLen; i++) {
        const e = data.expenses[i];
        const inc = data.incomes[i];
        aoa.push([
            e ? ('  '.repeat(e.depth || 0) + e.name) : '',
            e && e.amount !== undefined ? formatAmount(e.amount) : '',
            inc ? ('  '.repeat(inc.depth || 0) + inc.name) : '',
            inc && inc.amount !== undefined ? formatAmount(inc.amount) : ''
        ]);
    }
    aoa.push([]);
    aoa.push(['Total', formatAmount(data.totalExpenses), 'Total', formatAmount(data.totalIncomes)]);
  } else if (type === 'stock' || type === 'stockmovement') {
    filename = `StockMovement_${data.item?.name}.xlsx`;
    aoa.push(['Date', 'Particulars', 'Vch No.', 'Inward', 'Outward', 'Balance', 'Value']);
    (data.transactions || []).forEach((t: any) => {
        aoa.push([
            formatDateStr(t.date),
            t.particulars,
            t.vchNo || '',
            formatAmount(t.inward),
            formatAmount(t.outward),
            formatAmount(t.balance),
            formatAmount(t.value)
        ]);
    });
    aoa.push([]);
    aoa.push(['', 'Total', '', formatAmount(data.totals?.inward), formatAmount(data.totals?.outward), formatAmount(data.totals?.balance), formatAmount(data.totals?.value)]);
  } else if (type === 'stocksummary') {
    filename = `StockSummary_AsOf_${data.reportMeta?.toDate}.xlsx`;
    aoa.push(['Particulars', 'Quantity', 'Avg. Rate', 'Value']);
    (data.items || []).forEach((i: any) => {
        aoa.push([
            '  '.repeat(i.depth || 0) + i.name,
            i.quantity,
            formatAmount(i.rate),
            formatAmount(i.value)
        ]);
    });
    aoa.push([]);
    aoa.push(['Total', data.totals?.quantity || '', '', formatAmount(data.totals?.value)]);
  } else if (type === 'registersummary') {
    filename = `${data.reportMeta?.voucherType}_RegisterSummary_${data.reportMeta?.fromDate}_to_${data.reportMeta?.toDate}.xlsx`;
    aoa.push(['Month', 'Total Vouchers', 'Debit', 'Credit']);
    (data.months || []).forEach((m: any) => {
        aoa.push([
            m.month,
            m.voucherCount,
            formatAmount(m.totalDebit),
            formatAmount(m.totalCredit)
        ]);
    });
    aoa.push([]);
    aoa.push(['Total', data.totals?.totalVouchers || '', formatAmount(data.totals?.totalDebit), formatAmount(data.totals?.totalCredit)]);
  } else {
    alert('Export to Excel not fully implemented for this report.');
    return;
  }

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Formatting (Column widths & styles)
  // Apply bold to headers
  const numRows = aoa.length;
  let headerRowIndex = headerRows.length; // 0-indexed where table headers start
  for (let c = 0; c < 15; c++) {
    const cellRef = XLSX.utils.encode_cell({ r: headerRowIndex, c });
    if (ws[cellRef]) {
        ws[cellRef].s = { font: { bold: true } };
    }
  }
  
  // Make the report titles bold as well
  ws['A1'] = { v: companyName, t: 's', s: { font: { bold: true, sz: 14 } } };
  ws['A2'] = { v: reportTitle, t: 's', s: { font: { bold: true, sz: 12 } } };

  // Adjust column widths 
  const colWidths = aoa[headerRowIndex]?.map((_, colIndex) => {
      let maxLen = 10; // min width
      for (let r = headerRowIndex; r < numRows; r++) {
         const val = aoa[r][colIndex];
         if (val) {
             const strLen = String(val).length;
             if (strLen > maxLen) maxLen = strLen;
         }
      }
      return { wch: maxLen + 2 }; 
  }) || [];
  ws['!cols'] = colWidths;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Report");
  XLSX.writeFile(wb, filename);
};

export const exportToPdf = (type: string, data: any) => {
  const doc = new jsPDF();
  let filename = `${type}_export.pdf`;

  doc.setFontSize(14);
  doc.text(data.company.name, 14, 15);
  doc.setFontSize(10);

  if (type === 'daybook') {
    const isBinBox = data.reportMeta.title && data.reportMeta.title.includes('Bin Box');
    doc.text(`${data.reportMeta.title || 'Day Book'}: ${data.reportMeta.fromDate} to ${data.reportMeta.toDate}`, 14, 22);
    
    let body;
    let head;
    
    if (isBinBox) {
      head = [['Date', 'Particulars', 'Vch Type', 'Vch No.', 'Sys ID', 'Debit', 'Credit']];
      body = data.vouchers.map((v: any) => [
        v.date, v.particulars, v.vchType, v.vchNo, v.sysId, v.debit, v.credit
      ]);
      (doc as any).autoTable({
        startY: 30,
        head: head,
        body: body,
        foot: [['', 'Total', '', '', '', data.totals.debit, data.totals.credit]],
      });
    } else {
      head = [['Date', 'Particulars', 'Vch Type', 'Vch No.', 'Debit', 'Credit']];
      body = data.vouchers.map((v: any) => [
        v.date, v.particulars, v.vchType, v.vchNo, v.debit, v.credit
      ]);
      (doc as any).autoTable({
        startY: 30,
        head: head,
        body: body,
        foot: [['', 'Total', '', '', data.totals.debit, data.totals.credit]],
      });
    }
    
    filename = isBinBox ? `BinBox_${data.reportMeta.fromDate}_to_${data.reportMeta.toDate}.pdf` : `DayBook_${data.reportMeta.fromDate}_to_${data.reportMeta.toDate}.pdf`;
  } else if (type === 'trialbalance') {
    doc.text(`Trial Balance: ${data.reportMeta.fromDate} to ${data.reportMeta.toDate}`, 14, 22);
    
    const body = data.items.map((i: any) => [
      '  '.repeat(i.depth) + i.name,
      i.debit ? i.debit.toFixed(2) : '',
      i.credit ? i.credit.toFixed(2) : ''
    ]);

    (doc as any).autoTable({
      startY: 30,
      head: [['Particulars', 'Debit', 'Credit']],
      body: body,
      foot: [['Total', data.totalDebit, data.totalCredit]],
      styles: { cellPadding: 1, fontSize: 8 }
    });
    filename = `TrialBalance_${data.reportMeta.fromDate}_to_${data.reportMeta.toDate}.pdf`;
  } else if (type === 'balancesheet') {
    doc.text(`Balance Sheet as of ${data.reportMeta.toDate}`, 14, 22);
    
    const maxLen = Math.max(data.liabilities.length, data.assets.length);
    const body = [];
    for (let i = 0; i < maxLen; i++) {
        body.push([
            data.liabilities[i] ? ('  '.repeat(data.liabilities[i].depth) + data.liabilities[i].name) : '',
            data.liabilities[i] && data.liabilities[i].amount !== undefined ? data.liabilities[i].amount.toFixed(2) : '',
            data.assets[i] ? ('  '.repeat(data.assets[i].depth) + data.assets[i].name) : '',
            data.assets[i] && data.assets[i].amount !== undefined ? data.assets[i].amount.toFixed(2) : ''
        ]);
    }

    (doc as any).autoTable({
      startY: 30,
      head: [['Liabilities', 'Amount', 'Assets', 'Amount']],
      body: body,
      foot: [['Total', data.totalLiabilities, 'Total', data.totalAssets]],
    });
    filename = `BalanceSheet_AsOf_${data.reportMeta.toDate}.pdf`;
  } else if (type === 'profitloss') {
    doc.text(`Profit & Loss A/c: ${data.reportMeta.fromDate} to ${data.reportMeta.toDate}`, 14, 22);
    
    const maxLen = Math.max(data.expenses.length, data.incomes.length);
    const body = [];
    for (let i = 0; i < maxLen; i++) {
        body.push([
            data.expenses[i] ? ('  '.repeat(data.expenses[i].depth) + data.expenses[i].name) : '',
            data.expenses[i] && data.expenses[i].amount !== undefined ? data.expenses[i].amount.toFixed(2) : '',
            data.incomes[i] ? ('  '.repeat(data.incomes[i].depth) + data.incomes[i].name) : '',
            data.incomes[i] && data.incomes[i].amount !== undefined ? data.incomes[i].amount.toFixed(2) : ''
        ]);
    }

    (doc as any).autoTable({
      startY: 30,
      head: [['Particulars', 'Expenses', 'Particulars', 'Incomes']],
      body: body,
      foot: [['Total', data.totalExpenses, 'Total', data.totalIncomes]],
    });
    filename = `ProfitLoss_${data.reportMeta.fromDate}_to_${data.reportMeta.toDate}.pdf`;
  } else if (type === 'ledger') {
    doc.text(`Ledger Account: ${data.party.name}`, 14, 22);
    doc.text(`${data.reportMeta.fromDate} to ${data.reportMeta.toDate}`, 14, 28);
    
    const body = data.transactions.map((t: any) => [
      t.date, t.particulars.main !== undefined ? t.particulars.main : t.particulars, t.vchType || '', t.vchNo || '', t.debit, t.credit, t.balance
    ]);

    (doc as any).autoTable({
      startY: 35,
      head: [['Date', 'Particulars', 'Vch Type', 'Vch No.', 'Debit', 'Credit', 'Balance']],
      body: body,
      foot: [['', 'Total', '', '', data.totals.debit, data.totals.credit, data.totals.balance]]
    });
    filename = `Ledger_${data.party.name}.pdf`;
  } else if (type === 'stock') {
    doc.text(`Stock Movement: ${data.item.name} (${data.item.group})`, 14, 22);
    doc.text(`Format: ${data.reportMeta.dateRange}`, 14, 28);
    
    const body = data.transactions.map((t: any) => [
      t.date, t.particulars, t.vchNo || '', t.inward, t.outward, t.balance, t.value || ''
    ]);

    (doc as any).autoTable({
      startY: 35,
      head: [['Date', 'Particulars', 'Vch No.', 'Inward', 'Outward', 'Balance', 'Value']],
      body: body,
      foot: [['', 'Total', '', data.totals.inward, data.totals.outward, data.totals.balance, data.totals.value]]
    });
    filename = `StockMovement_${data.item.name}.pdf`;
  } else if (type === 'stocksummary') {
    doc.text(`Stock Summary`, 14, 22);
    doc.text(`As of ${data.reportMeta.toDate}`, 14, 28);
    
    const body = data.items.map((i: any) => [
      '  '.repeat(i.depth) + i.name, i.quantity, i.rate ? i.rate.toFixed(2) : '', i.value ? i.value.toFixed(2) : ''
    ]);

    (doc as any).autoTable({
      startY: 35,
      head: [['Particulars', 'Quantity', 'Avg. Rate', 'Value']],
      body: body,
      foot: [['Total', data.totals.quantity, '', data.totals.value]]
    });
    filename = `StockSummary_AsOf_${data.reportMeta.toDate}.pdf`;
  } else if (type === 'registersummary') {
    doc.text(`${data.reportMeta.voucherType} Register Summary`, 14, 22);
    doc.text(`${data.reportMeta.fromDate} to ${data.reportMeta.toDate}`, 14, 28);
    
    const body = data.months.map((m: any) => [
      m.month, m.voucherCount, m.totalDebit.toFixed(2), m.totalCredit.toFixed(2)
    ]);

    (doc as any).autoTable({
      startY: 35,
      head: [['Month', 'Vouchers', 'Debit', 'Credit']],
      body: body,
      foot: [['Total', data.totals.totalVouchers, data.totals.totalDebit, data.totals.totalCredit]]
    });
    filename = `${data.reportMeta.voucherType}_RegisterSummary_${data.reportMeta.fromDate}_to_${data.reportMeta.toDate}.pdf`;
  } else if (type === 'voucherregister') {
    doc.text(`${data.reportMeta.voucherType} Register`, 14, 22);
    doc.text(`Period: ${data.reportMeta.period}`, 14, 28);
    
    const body = data.vouchers.map((v: any) => [
      v.date, v.particulars, v.vchType, v.vchNo, v.debit, v.credit
    ]);

    (doc as any).autoTable({
      startY: 35,
      head: [['Date', 'Particulars', 'Vch Type', 'Vch No.', 'Debit', 'Credit']],
      body: body,
      foot: [['', 'Total', '', '', data.totals.totalDebit, data.totals.totalCredit]]
    });
    filename = `${data.reportMeta.voucherType}_Register_${data.reportMeta.period}.pdf`;
  } else if (type === 'invoice') {
    doc.text(`Invoice: ${data.meta.invoiceNo}`, 14, 22);
    doc.text(`Date: ${data.meta.date}`, 14, 28);
    doc.text(`To: ${data.buyer.name}`, 14, 34);

    const body = data.items.map((i: any) => [
      i.slNo, i.description, i.quantity, i.rate, i.per, i.amount
    ]);

    (doc as any).autoTable({
      startY: 45,
      head: [['Sl No.', 'Description', 'Quantity', 'Rate', 'Per', 'Amount']],
      body: body,
      foot: [['', 'Total', data.totals.quantity, '', '', data.totals.amount]]
    });
    
    // Add amount in words
    const finalY = (doc as any).lastAutoTable.finalY || 150;
    doc.text(`Amount Chargeable (in words): ${data.totals.amountInWords}`, 14, finalY + 10);
    filename = `Invoice_${data.meta.invoiceNo}.pdf`;
  } else {
    alert('Export to PDF not fully implemented for this report.');
    return;
  }

  doc.save(filename);
};
